import { categorizeFood } from '@/lib/utils/foodCategorizer'
import { standardizeQuantity } from '@/lib/utils/unitStandardizer'
import {
  FoodCategory,
  NutritionalValue,
  Quantity,
  Unit,
  createCalories,
  createGrams,
  createMilligrams,
} from '@/types/nutrition'
import { InvalidFoodItemError, InvalidNutritionalValueError } from '@/types/parser'

/**
 * Specialized parser for individual food items.
 *
 * Handles complex food description parsing, brand extraction using pattern matching,
 * quantity parsing with unit normalization, and nutritional value extraction with validation.
 *
 * @example
 * ```typescript
 * const parser = new FoodItemParser()
 * const description = parser.parseFoodDescription('IGA 2% Milk - 2% Milk, 350 milliliter')
 * const nutrition = parser.parseNutritionalValues(['178', '16g', '7g', ...])
 * ```
 */
export class FoodItemParser {
  /**
   * Parses array of nutrition value strings into typed structure.
   *
   * @param values - Array of exactly 8 nutrition values: [calories, carbs, fat, protein, cholesterol, sodium, sugar, fiber]
   * @returns NutritionalValue with strongly-typed nutrition data
   * @throws InvalidFoodItemError When array doesn't contain exactly 8 values or values are malformed
   *
   * @example
   * ```typescript
   * const nutrition = parser.parseNutritionalValues([
   *   '178', '16g', '7g', '12g', '37mg', '178mg', '16g', '0g'
   * ])
   * // Returns: NutritionalValue with typed calories, carbs, etc.
   * ```
   */
  parseNutritionalValues(values: readonly string[]): NutritionalValue {
    if (values.length !== 8) {
      throw new InvalidFoodItemError(
        values.join(', '),
        undefined,
        `Expected 8 nutritional values, got ${values.length}`
      )
    }

    // After length validation, we can safely destructure as a tuple
    const [
      caloriesStr,
      carbsStr,
      fatStr,
      proteinStr,
      cholesterolStr,
      sodiumStr,
      sugarStr,
      fiberStr,
    ] = values as readonly [string, string, string, string, string, string, string, string]

    try {
      const calories = this.parseCalories(caloriesStr)
      const carbs = this.parseGrams(carbsStr)
      const fat = this.parseGrams(fatStr)
      const protein = this.parseGrams(proteinStr)
      const cholesterol = this.parseMilligrams(cholesterolStr)
      const sodium = this.parseMilligrams(sodiumStr)
      const sugar = this.parseGrams(sugarStr)
      const fiber = this.parseGrams(fiberStr)

      return {
        calories,
        carbs,
        fat,
        protein,
        cholesterol,
        sodium,
        sugar,
        fiber,
      }
    } catch (error) {
      if (error instanceof InvalidNutritionalValueError) {
        throw new InvalidFoodItemError(values.join(', '), undefined, error.message)
      }
      throw error
    }
  }

  /**
   * Extracts name, quantity, and category from food description.
   *
   * Handles various formats without brand extraction, treating the entire text
   * before the last comma as the product name, and the last part as quantity.
   * Automatically assigns food category based on the parsed name.
   *
   * @param description - Food description string with name and quantity
   * @returns Object containing parsed name, quantity, and assigned category
   * @throws InvalidFoodItemError When description format is invalid or missing required components
   *
   * @example
   * ```typescript
   * const result = parser.parseFoodDescription('IGA 2% Milk - 2% Milk, 350 milliliter')
   * // { name: 'IGA 2% Milk - 2% Milk', quantity: { amount: 350, unit: 'ml' }, category: 'Dairy & Eggs' }
   *
   * const result = parser.parseFoodDescription('Apple, 110 gram')
   * // { name: 'Apple', quantity: { amount: 110, unit: 'g' }, category: 'Fruits and Vegetables' }
   *
   * const result = parser.parseFoodDescription('Fish, mackerel, Atlantic, raw, 160 gram')
   * // { name: 'Fish, mackerel, Atlantic, raw', quantity: { amount: 160, unit: 'g' }, category: 'Meat, Poultry, Fish & Seafood' }
   * ```
   */
  parseFoodDescription(description: string): {
    name: string
    quantity: Quantity
    category: FoodCategory
  } {
    if (!description || description.trim() === '') {
      throw new InvalidFoodItemError(description, undefined, 'Empty food description')
    }

    // Split by comma to separate name/brand from quantity
    // The quantity is always the last part after the last comma
    const parts = description.split(',').map((part) => part.trim())
    if (parts.length < 2) {
      throw new InvalidFoodItemError(
        description,
        undefined,
        'Food description must contain name and quantity separated by comma'
      )
    }

    // The quantity is the last part - safe access after length validation
    const quantityStr = parts.at(-1)
    if (!quantityStr) {
      throw new InvalidFoodItemError(description, undefined, 'Missing quantity in food description')
    }
    // Everything else is the product name (join them back with commas)
    const name = parts.slice(0, -1).join(', ')

    const quantity = this.parseQuantity(quantityStr)
    const category = categorizeFood(name)

    return { name, quantity, category }
  }

  parseQuantity(quantityStr: string): Quantity {
    if (!quantityStr || quantityStr.trim() === '') {
      throw new InvalidFoodItemError(quantityStr, undefined, 'Empty quantity string')
    }

    // Handle patterns like "300 gram(s)", "1 medium (7\" to 7-7/8\" long)", etc.
    const cleanQuantity = quantityStr
      .replace(/\([^)]*\)/g, '') // Remove parenthetical descriptions
      .trim()

    // Match number followed by unit
    const quantityMatch = cleanQuantity.match(/^(\d+(?:\.\d+)?)\s+(.+)$/)
    if (!quantityMatch) {
      throw new InvalidFoodItemError(
        quantityStr,
        undefined,
        'Invalid quantity format. Expected: "number unit"'
      )
    }

    // After regex match validation, we can safely destructure capture groups
    const [, amountStr, unitStr] = quantityMatch as [string, string, string]
    const amount = parseFloat(amountStr)
    const rawUnit = unitStr.toLowerCase().trim()

    // Create temporary quantity and standardize it
    const tempQuantity: Quantity = { amount, unit: rawUnit as Unit }
    const standardizedQuantity = standardizeQuantity(tempQuantity)

    return standardizedQuantity
  }

  private parseCalories(value: string): ReturnType<typeof createCalories> {
    // Handle -- placeholder values by defaulting to 0
    if (value === '--') {
      return createCalories(0)
    }

    const num = parseFloat(value)
    if (isNaN(num)) {
      throw new InvalidNutritionalValueError(value, 'calories')
    }
    return createCalories(num)
  }

  private parseGrams(value: string): ReturnType<typeof createGrams> {
    // Handle -- placeholder values by defaulting to 0
    if (value === '--') {
      return createGrams(0)
    }

    // Remove 'g' suffix and parse
    const cleanValue = value.replace(/g$/, '').trim()
    const num = parseFloat(cleanValue)
    if (isNaN(num)) {
      throw new InvalidNutritionalValueError(value, 'grams')
    }
    return createGrams(num)
  }

  private parseMilligrams(value: string): ReturnType<typeof createMilligrams> {
    // Handle -- placeholder values by defaulting to 0
    if (value === '--') {
      return createMilligrams(0)
    }

    // Remove 'mg' suffix and parse
    const cleanValue = value.replace(/mg$/, '').trim()
    const num = parseFloat(cleanValue)
    if (isNaN(num)) {
      throw new InvalidNutritionalValueError(value, 'milligrams')
    }
    return createMilligrams(num)
  }
}
